/**
 * Template API Service
 * Handles template storage using AWS API Gateway and DynamoDB
 * Provides cross-device template synchronization
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import axios from 'axios';

// API Base URL - Cross-Device Template Storage
const API_BASE_URL = process.env.REACT_APP_TEMPLATE_API_URL || 'https://l8f63wfxge.execute-api.us-east-2.amazonaws.com/prod/templates';

class TemplateApiService {
  /**
   * Get authorization header with Cognito JWT token
   * @returns {Promise<Object>} Authorization header object
   */
  async getAuthHeaders() {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        throw new Error('No authentication token available');
      }

      return {
        'Content-Type': 'application/json',
        'Authorization': token
      };
    } catch (error) {
      console.error('Failed to get auth headers:', error);
      throw new Error('Authentication required');
    }
  }

  /**
   * Fetch all templates for the current user from API
   * @returns {Promise<Array>} Array of template objects
   */
  async fetchTemplates() {
    // IMPORTANT: this method must THROW on failure. Previously it returned [] on
    // any error, which made an outage indistinguishable from "user has no
    // templates" — that caused the app to initialize defaults and destructively
    // overwrite the user's real cloud templates. Callers now distinguish an empty
    // (but reachable) cloud from an unreachable one.
    const headers = await this.getAuthHeaders();

    const response = await axios.get(API_BASE_URL, { headers });

    if (response.data && response.data.success) {
      return response.data.templates || [];
    }

    // Unexpected response shape — treat as an error, never as "empty".
    throw new Error('Unexpected response while fetching templates');
  }

  /**
   * Create a new template via API
   * @param {Object} templateData - Template data (name, noteType, fields)
   * @returns {Promise<Object|null>} Created template or null on failure
   */
  async createTemplate(templateData) {
    try {
      const headers = await this.getAuthHeaders();

      const response = await axios.post(API_BASE_URL, templateData, { headers });

      if (response.data && response.data.success) {
        return response.data.template;
      }

      return null;
    } catch (error) {
      console.error('Failed to create template via API:', error);
      throw error;
    }
  }

  /**
   * Update an existing template via API
   * @param {string} templateId - Template ID to update
   * @param {Object} templateData - Updated template data
   * @returns {Promise<Object|null>} Updated template or null on failure
   */
  async updateTemplate(templateId, templateData) {
    try {
      const headers = await this.getAuthHeaders();

      const response = await axios.put(
        `${API_BASE_URL}/${templateId}`,
        templateData,
        { headers }
      );

      if (response.data && response.data.success) {
        return response.data.template;
      }

      return null;
    } catch (error) {
      console.error('Failed to update template via API:', error);
      throw error;
    }
  }

  /**
   * Delete a template via API
   * @param {string} templateId - Template ID to delete
   * @returns {Promise<boolean>} Success status
   */
  async deleteTemplate(templateId) {
    try {
      const headers = await this.getAuthHeaders();

      const response = await axios.delete(
        `${API_BASE_URL}/${templateId}`,
        { headers }
      );

      return response.data && response.data.success;
    } catch (error) {
      console.error('Failed to delete template via API:', error);
      throw error;
    }
  }

  /**
   * Set a template as the default template via API
   * @param {string} templateId - Template ID to set as default
   * @returns {Promise<boolean>} Success status
   */
  async setDefaultTemplate(templateId) {
    try {
      const headers = await this.getAuthHeaders();

      const response = await axios.post(
        `${API_BASE_URL}/${templateId}/default`,
        {},
        { headers }
      );

      return response.data && response.data.success;
    } catch (error) {
      console.error('Failed to set default template via API:', error);
      throw error;
    }
  }

  /**
   * Check if API is available
   * @returns {Promise<boolean>} True if API is available
   */
  async isApiAvailable() {
    try {
      // Quick check - if we can get auth headers, API should be accessible
      await this.getAuthHeaders();
      return true;
    } catch (error) {
      console.warn('API not available:', error.message);
      return false;
    }
  }

  /**
   * Migrate templates from localStorage to API
   * @param {Array} localTemplates - Templates from localStorage
   * @returns {Promise<Object>} Migration result with success status and counts
   */
  async migrateLocalTemplates(localTemplates) {
    if (!localTemplates || localTemplates.length === 0) {
      return { success: true, migrated: 0, failed: 0 };
    }

    let migrated = 0;
    let failed = 0;

    for (const template of localTemplates) {
      try {
        // Create template in API (it will get a new templateId from server)
        await this.createTemplate({
          name: template.name,
          noteType: template.noteType || 'progress',
          fields: template.fields
        });
        migrated++;
      } catch (error) {
        console.error('Failed to migrate template:', template.name, error);
        failed++;
      }
    }

    return {
      success: failed === 0,
      migrated,
      failed,
      total: localTemplates.length
    };
  }
}

// Create and export singleton instance
export const templateApiService = new TemplateApiService();
export default templateApiService;
